import React, { useState, useEffect } from 'react';
import { useAlert } from '../context/AlertContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Plus, Trash2, Edit, Search, UserCircle, Link as LinkIcon, ChevronRight } from 'lucide-react';
import { Table, Button, InputGroup, Form, Card, Row, Col, Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatPhoneBrazilian } from '../utils/formatters';
import { useClipboard } from '../hooks/useClipboard';
import './ClientList.css';


interface Dependent {
    id: number;
    name: string;
}

interface Client {
    id: number;
    name: string;
    phone: string;
    email: string;
    observation: string;
    photo?: string;
    dependents?: Dependent[];
}

const ClientList = () => {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [clients, setClients] = useState<Client[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const { copied: copiedClientId, copyToClipboard } = useClipboard();

    useEffect(() => {
        fetchClients();
    }, []);

    const fetchClients = async () => {
        try {
            const response = await api.get('/clients/');
            setClients(response.data);
        } catch (error) {
            console.error('Error fetching clients:', error);
        }
    };

    const getClientScheduleUrl = (phone: string) => {
        const cleanPhone = phone.replace(/\D/g, '');
        return `${window.location.origin}/public/schedule?phone=${cleanPhone}`;
    };

    const handleDelete = (id: number) => {
        showAlert('Tem certeza que deseja excluir?', {
            title: 'Confirmação',
            confirmable: true,
            confirmText: 'Excluir',
            confirmVariant: 'btn-primary',
            onConfirm: async () => {
                try {
                    await api.delete(`/clients/${id}/`);
                    fetchClients();
                } catch (error) {
                    console.error('Error deleting client:', error);
                }
            }
        });
    };

    const filteredClients = clients.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone.includes(searchTerm) ||
        (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <Card className="shadow-sm border-0">
            <Card.Body>
                <Row className="mb-4 align-items-center">
                    <Col>
                        <h3 className="mb-0">Clientes</h3>
                    </Col>
                    <Col xs="auto">
                        <Button variant="success" onClick={() => navigate('/clients/new')} className="d-flex align-items-center gap-2">
                            <Plus size={18} /> Novo Cliente
                        </Button>
                    </Col>
                </Row>

                <InputGroup className="mb-4">
                    <InputGroup.Text className="bg-white border-end-0">
                        <Search size={18} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                        placeholder="Pesquisar por nome, telefone ou email..."
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                        className="border-start-0 ps-0"
                    />
                </InputGroup>

                {/* Desktop: Tabela */}
                <div className="custom-table-container d-none d-md-block">
                    <Table hover className="custom-table mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>Foto</th>
                                <th>Nome</th>
                                <th>Telefone</th>
                                <th>Email</th>
                                <th>Dependentes</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredClients.map((client) => (
                                <tr
                                    key={client.id}
                                >
                                    <td onClick={() => navigate(`/clients/${client.id}`)} style={{ cursor: 'pointer' }}>
                                        <div className="d-flex align-items-center gap-3">
                                            {client.photo ? (
                                                <Image
                                                    src={client.photo}
                                                    roundedCircle
                                                    width={40}
                                                    height={40}
                                                    style={{ objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="bg-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                                                    <UserCircle size={24} className="text-secondary" />
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td onClick={() => navigate(`/clients/${client.id}`)} style={{ cursor: 'pointer' }} className="fw-medium align-middle">{client.name}</td>
                                    <td onClick={() => navigate(`/clients/${client.id}`)} style={{ cursor: 'pointer' }} className="align-middle">{formatPhoneBrazilian(client.phone)}</td>
                                    <td onClick={() => navigate(`/clients/${client.id}`)} style={{ cursor: 'pointer' }} className="align-middle">{client.email || '-'}</td>
                                    <td className="align-middle">
                                        {client.dependents && client.dependents.length > 0 ? (
                                            <span className="badge bg-info">
                                                {client.dependents.length} {client.dependents.length === 1 ? 'dependente' : 'dependentes'}
                                            </span>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td className="text-end align-middle">
                                        <OverlayTrigger 
                                            placement="top" 
                                            overlay={<Tooltip id={`link-${client.id}`}>Copiar link de agendamento para este cliente</Tooltip>}
                                        >
                                            <Button
                                                variant="link"
                                                className="text-primary p-1 me-2"
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    copyToClipboard(getClientScheduleUrl(client.phone));
                                                }}
                                            >
                                                <LinkIcon size={18} />
                                            </Button>
                                        </OverlayTrigger>
                                        <Button
                                            variant="link"
                                            className="text-secondary p-1 me-2"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                navigate(`/clients/${client.id}`);
                                            }}
                                        >
                                            <Edit size={18} />
                                        </Button>
                                        <Button
                                            variant="link"
                                            className="text-secondary p-1"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                handleDelete(client.id);
                                            }}
                                        >
                                            <Trash2 size={18} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {filteredClients.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="text-center py-4 text-muted">
                                        Nenhum cliente encontrado.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                {/* Mobile: Lista de Cards */}
                <div className="client-list-mobile d-md-none">
                    {filteredClients.length > 0 ? (
                        filteredClients.map((client) => (
                            <div 
                                key={client.id} 
                                className="client-card-mobile"
                            >
                                <div 
                                    className="client-card-mobile-content"
                                    onClick={() => navigate(`/clients/${client.id}`)}
                                >
                                    {client.photo ? (
                                        <Image
                                            src={client.photo}
                                            roundedCircle
                                            width={48}
                                            height={48}
                                            style={{ objectFit: 'cover' }}
                                            className="client-card-mobile-photo"
                                        />
                                    ) : (
                                        <div className="client-card-mobile-photo-placeholder">
                                            <UserCircle size={28} className="text-secondary" />
                                        </div>
                                    )}
                                    <div className="client-card-mobile-info">
                                        <span className="client-card-mobile-name">{client.name}</span>
                                        <div className="client-card-mobile-details">
                                            <span className="client-card-mobile-phone">{formatPhoneBrazilian(client.phone)}</span>
                                            {client.dependents && client.dependents.length > 0 && (
                                                <span className="client-card-mobile-dependents">
                                                    {client.dependents.length} {client.dependents.length === 1 ? 'dep.' : 'deps.'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <ChevronRight size={20} className="text-muted" />
                                </div>
                                <div className="client-card-mobile-actions">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="client-card-mobile-btn"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            navigate(`/clients/${client.id}`);
                                        }}
                                    >
                                        <Edit size={16} /> Editar
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="client-card-mobile-btn"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            handleDelete(client.id);
                                        }}
                                    >
                                        <Trash2 size={16} /> Excluir
                                    </Button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-4 text-muted">
                            Nenhum cliente encontrado.
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

export default ClientList;
